"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BasicAnalysisEngine = void 0;
const LetterPaths_1 = require("../utils/LetterPaths");
class BasicAnalysisEngine {
    async recognizeHandwriting(strokes) {
        return { text: "?", confidence: 0 };
    }
    async analyzeCraftsmanship(strokes, targetText) {
        const rawTarget = (targetText || 'A').trim();
        const normalizedTarget = rawTarget.toUpperCase();
        const allUserPoints = strokes.flatMap(s => s.points || []);
        if (allUserPoints.length === 0) {
            return {
                score: 0,
                feedback: "Canvas is empty! Draw the letter or sentence to get a score.",
                suggestions: ["Use your finger or stylus to write on the canvas"],
                details: {}
            };
        }
        // ── 1. Single Letter Tracing in Library ──
        if (LetterPaths_1.LETTER_PATHS[normalizedTarget]) {
            const targetDef = LetterPaths_1.LETTER_PATHS[normalizedTarget];
            // 1. Calculate Bounds
            let tMinX = Infinity, tMinY = Infinity, tMaxX = -Infinity, tMaxY = -Infinity;
            targetDef.strokes.forEach(s => s.forEach(p => {
                tMinX = Math.min(tMinX, p.x);
                tMinY = Math.min(tMinY, p.y);
                tMaxX = Math.max(tMaxX, p.x);
                tMaxY = Math.max(tMaxY, p.y);
            }));
            let uMinX = Infinity, uMinY = Infinity, uMaxX = -Infinity, uMaxY = -Infinity;
            allUserPoints.forEach(p => {
                uMinX = Math.min(uMinX, p.x);
                uMinY = Math.min(uMinY, p.y);
                uMaxX = Math.max(uMaxX, p.x);
                uMaxY = Math.max(uMaxY, p.y);
            });
            const targetWidth = Math.max(1, tMaxX - tMinX);
            const targetHeight = Math.max(1, tMaxY - tMinY);
            const userWidth = Math.max(1, uMaxX - uMinX);
            const userHeight = Math.max(1, uMaxY - uMinY);
            // 2. Define Grid (12x12)
            const GRID_SIZE = 12;
            const targetGrid = new Set();
            const userGrid = new Set();
            // Rasterize Target
            targetDef.strokes.forEach(stroke => {
                this.rasterizeStroke(stroke, targetGrid, tMinX, tMinY, targetWidth, targetHeight, GRID_SIZE);
            });
            // Rasterize User
            strokes.forEach(stroke => {
                if (stroke.points && stroke.points.length > 0) {
                    this.rasterizeStroke(stroke.points, userGrid, uMinX, uMinY, userWidth, userHeight, GRID_SIZE);
                }
            });
            // 3. Jaccard & Coverage Index
            let intersection = 0;
            userGrid.forEach(cell => {
                if (targetGrid.has(cell))
                    intersection++;
            });
            const union = new Set([...targetGrid, ...userGrid]).size;
            const jaccard = union === 0 ? 0 : intersection / union;
            const targetCoverage = targetGrid.size === 0 ? 0 : intersection / targetGrid.size;
            // Aspect ratio similarity check (prevents a 1D straight line from falsely scoring high on a 2D letter)
            const targetAspect = targetWidth / targetHeight;
            const userAspect = userWidth / userHeight;
            const aspectSimilarity = Math.min(targetAspect, userAspect) / Math.max(targetAspect, userAspect);
            // Calculate score with penalties for missing parts or single straight line
            let score;
            if (targetCoverage < 0.4 || aspectSimilarity < 0.35) {
                // Low coverage or extreme aspect mismatch (e.g. single straight line)
                score = Math.round(targetCoverage * 40 * aspectSimilarity);
            }
            else {
                score = Math.round(Math.pow(jaccard, 0.7) * 85 + targetCoverage * 15);
                if (aspectSimilarity < 0.6)
                    score = Math.round(score * aspectSimilarity);
                if (targetCoverage > 0.8 && score < 95)
                    score += 5;
            }
            // Stroke count alignment
            const expectedStrokes = targetDef.strokes.length;
            if (strokes.length < expectedStrokes && targetCoverage < 0.6) {
                score = Math.min(score, 25);
            }
            score = Math.max(0, Math.min(100, score));
            let feedback = "Great job!";
            if (score < 30)
                feedback = `Slow down and trace over all parts of letter ${normalizedTarget}.`;
            else if (score < 70)
                feedback = "Good effort! Try to stay inside the lines and complete the full letter.";
            else if (score >= 90)
                feedback = "Excellent! Nearly perfect trace.";
            return {
                score,
                feedback,
                suggestions: score < 70
                    ? ["Follow all dotted lines carefully", "Ensure all parts of the letter are drawn"]
                    : ["Ready for the next one!"],
                details: {
                    jaccard: jaccard.toFixed(2),
                    targetCoverage: targetCoverage.toFixed(2),
                    aspectSimilarity: aspectSimilarity.toFixed(2),
                }
            };
        }
        // ── 2. Multi-word / Sentence Copy Writing Evaluation ──
        const words = rawTarget.split(/\s+/).filter(Boolean);
        const totalChars = rawTarget.replace(/\s+/g, '').length;
        const strokeCount = strokes.length;
        const totalPoints = allUserPoints.length;
        // Calculate total stroke travel length
        let totalStrokeLength = 0;
        strokes.forEach(s => {
            if (!s.points || s.points.length < 2)
                return;
            for (let i = 1; i < s.points.length; i++) {
                const dx = s.points[i].x - s.points[i - 1].x;
                const dy = s.points[i].y - s.points[i - 1].y;
                totalStrokeLength += Math.sqrt(dx * dx + dy * dy);
            }
        });
        // Bounding box of user writing
        let uMinX = Infinity, uMinY = Infinity, uMaxX = -Infinity, uMaxY = -Infinity;
        allUserPoints.forEach(p => {
            uMinX = Math.min(uMinX, p.x);
            uMinY = Math.min(uMinY, p.y);
            uMaxX = Math.max(uMaxX, p.x);
            uMaxY = Math.max(uMaxY, p.y);
        });
        const userWidth = Math.max(1, uMaxX - uMinX);
        // A single straight line or very few strokes for a multi-word sentence
        if (words.length > 1 && (strokeCount <= 2 || totalPoints < 25 || totalStrokeLength < 80)) {
            return {
                score: 10,
                feedback: "Only a quick mark was detected. Please copy the complete sentence.",
                suggestions: ["Write out every word on the lined canvas", "Take your time with each letter"],
                details: { strokeCount, totalStrokeLength: Math.round(totalStrokeLength) }
            };
        }
        // Multi-word writing scoring heuristic based on length, stroke consistency, and width
        const expectedMinStrokes = Math.max(3, Math.round(totalChars * 0.4));
        const strokeRatio = Math.min(1.0, strokeCount / expectedMinStrokes);
        const pointsPerWord = totalPoints / Math.max(1, words.length);
        let multiScore = 50;
        if (strokeRatio >= 0.8 && pointsPerWord >= 20 && userWidth > 150) {
            multiScore = 85;
        }
        else if (strokeRatio >= 0.5 && pointsPerWord >= 12) {
            multiScore = 70;
        }
        else {
            multiScore = Math.max(15, Math.round(strokeRatio * 60));
        }
        return {
            score: Math.min(100, multiScore),
            feedback: multiScore >= 80 ? "Neat handwriting! Well done." : "Good effort! Keep practicing your letter formation.",
            suggestions: multiScore < 75
                ? ["Use the guidelines to keep letters evenly sized", "Leave spaces between words"]
                : ["Ready for the next task!"],
            details: {
                strokeCount,
                wordCount: words.length,
                totalPoints,
                userWidth: Math.round(userWidth),
            }
        };
    }
    rasterizeStroke(points, grid, minX, minY, width, height, gridSize) {
        if (points.length === 0)
            return;
        const safeW = width || 1;
        const safeH = height || 1;
        const processPoint = (px, py) => {
            const nx = (px - minX) / safeW;
            const ny = (py - minY) / safeH;
            const gx = Math.min(gridSize - 1, Math.max(0, Math.floor(nx * gridSize)));
            const gy = Math.min(gridSize - 1, Math.max(0, Math.floor(ny * gridSize)));
            grid.add(`${gx},${gy}`);
            // 3x3 Dilation for forgiveness
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    const ngx = gx + dx;
                    const ngy = gy + dy;
                    if (ngx >= 0 && ngx < gridSize && ngy >= 0 && ngy < gridSize) {
                        grid.add(`${ngx},${ngy}`);
                    }
                }
            }
        };
        if (points.length === 1) {
            processPoint(points[0].x, points[0].y);
            return;
        }
        for (let i = 0; i < points.length - 1; i++) {
            const p1 = points[i];
            const p2 = points[i + 1];
            const dist = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
            const steps = Math.ceil(dist / 2);
            for (let j = 0; j <= steps; j++) {
                const t = j / steps;
                processPoint(p1.x + (p2.x - p1.x) * t, p1.y + (p2.y - p1.y) * t);
            }
        }
    }
}
exports.BasicAnalysisEngine = BasicAnalysisEngine;
